import { api } from '@/utils/api';
import { apiNl } from '@/utils/apiNl';
import { ResumeDigitalTwin } from './DigitalTwin';
import { sanitizePathParam } from '@/utils/sanitize';

export default class DigitalTwinRepository {
	private buildDigitalTwinUploadFormData(params: {
		hydroTwinName: string;
		inputParameters: string;
		assetId: string;
		outputParam: string;
		inpId: number;
		inpName: string;
		objectType: string;
		objectModelId: string;
		h5File: File;
		hdhtFile: File;
	}) {
		const formData = new FormData();
		const requestPayload = {
			hydroTwinName: params.hydroTwinName,
			inputParameters: params.inputParameters,
			assetId: params.assetId,
			inpId: params.inpId,
			inpName: params.inpName,
			objectType: params.objectType,
			objectModelId: params.objectModelId,
			outputParam: params.outputParam,
		};
		const requestBlob = new Blob([JSON.stringify(requestPayload)], {
			type: 'application/json',
		});

		formData.append('request', requestBlob);
		formData.append('h5File', params.h5File);
		formData.append('hdhtFile', params.hdhtFile);

		return formData;
	}

	async postDigitalTwin(data: any, infrastructureId: any, parameterId: any): Promise<boolean> {
		const iid = sanitizePathParam(infrastructureId);
		const pid = sanitizePathParam(parameterId);
		const bulkIot = await api.post(
			`digitaltwins/neuralnetworks/infrastructures/${iid}/parameters/${pid}/train`,
			data,
		);
		return bulkIot.data;
	}

	async uploadDigitalTwin(params: {
		hydroTwinName: string;
		inputParameters: string;
		assetId: string;
		outputParam: string;
		inpId: number;
		inpName: string;
		objectType: string;
		objectModelId: string;
		h5File: File;
		hdhtFile: File;
	}): Promise<any> {
		const formData = this.buildDigitalTwinUploadFormData(params);

		try {
			const response = await api.post(`digitaltwins/upload`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
			return response.data;
		} catch (error) {
			console.error(error);
			throw error;
		}

	}

	async updateUploadDigitalTwin(
		neuralNetworkId: number | string,
		params: {
			h5File: File;
			hdhtFile: File;
		},
	): Promise<any> {
		const nid = sanitizePathParam(neuralNetworkId);
		const formData = new FormData();
		formData.append('h5File', params.h5File);
		formData.append('hdhtFile', params.hdhtFile);

		const response = await api.post(`digitaltwins/neuralnetworks/${nid}/upload`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data;
	}

	async getDigitalTwins(
		infrastructureId: number | string,
		paramId: number | string,
		loading: boolean = true,
	) {
		const axiosInstance = loading ? api : apiNl;
		const iid = sanitizePathParam(infrastructureId);
		const pid = sanitizePathParam(paramId);
		const response = await axiosInstance.get(
			`digitaltwins/neuralnetworks/infrastructures/${iid}/params/${pid}`,
		);
		return response.data;
	}

	async deleteDigitalTwin(neuralNetworkId: any, parameterId: any): Promise<boolean> {
		const nid = sanitizePathParam(neuralNetworkId);
		const pid = sanitizePathParam(parameterId);
		const response = await api.delete(`digitaltwins/neuralnetworks/${nid}/parameter/${pid}`);
		return response.data;
	}

	async getTestModel(neuralNetworkId: number | string): Promise<boolean> {
		const nid = sanitizePathParam(neuralNetworkId);
		const response = await api.get(`/digitaltwins/neuralnetworks/${nid}/testModel`);
		return response.data;
	}

	async setFavoriteModel(neuralNetworkId: number | string): Promise<boolean> {
		const nid = sanitizePathParam(neuralNetworkId);
		const response = await api.patch(`digitaltwins/neuralnetworks/${nid}/fav`);
		return response.data;
	}

	async getDefaultConfiguration() {
		const response = await api.get(`digitaltwins/neuralnetworks/advancedConfiguration`);
		return response.data;
	}

	async postConfiguration({
		parameterId,
		data,
	}: {
		parameterId: number | string;
		data: {
			inpId: number;
			inpObjectId: number;
			inpObjectType: number;
		};
	}) {
		const pid = sanitizePathParam(parameterId);
		const response = await api.post(
			`measurements/parameters/${pid}/neuralNetwork/configuration`,
			data,
		);
		return response.data;
	}

	async getConfiguration({ paramId, type }: { paramId: number; type: string }) {
		const pid = sanitizePathParam(paramId);
		const t = sanitizePathParam(type);
		const response = await api.get(`modelConfiguration/${pid}/${t}`);
		return response.data;
	}

  async getDigitalTwinsResume(params: { locationId: number}): Promise<ResumeDigitalTwin[]> {
    // const response = await mockFlyApi.get<ResumeDigitalTwin[]>('/bff/digital_twins/resume', { params });
	const response = await api.get<ResumeDigitalTwin[]>('/digitaltwins/resume', { params });
    return response.data;
  }

  async getGisFilesByInpId(inpId: number): Promise<GeoJSON.FeatureCollection[]> {
    const id = sanitizePathParam(inpId);
    const response = await api.get<GeoJSON.FeatureCollection[]>(`/inps/${id}/geojson`);
    return response.data;
  }
}
